type ClientDebugLevel = "debug" | "info" | "warn" | "error";

type ClientDebugEvent = {
  event: string;
  level?: ClientDebugLevel;
  details?: unknown;
};

const MAX_STRING_LENGTH = 2_000;
const MAX_ARRAY_ITEMS = 30;
const MAX_DEPTH = 5;
const DUPLICATE_WINDOW_MS = 5_000;
const SENSITIVE_KEY =
  /(?:authorization|cookie|password|passphrase|secret|token|api[-_]?key|otp|totp|backup|credential|smtp|email|phone|content|body|description|notes?)/i;
const CREDENTIAL_URL = /\b([a-z][a-z0-9+.-]*):\/\/[^@\s/]+@/gi;
const EMAIL_VALUE = /\b[A-Z0-9._%+-]{1,64}@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const recentFingerprints = new Map<string, number>();

declare global {
  interface Window {
    __enkeyClientObservabilityInstalled?: boolean;
  }
}

function safeString(value: string) {
  return [
    ...value
      .replace(CREDENTIAL_URL, "$1://[credentials]@")
      .replace(EMAIL_VALUE, "[email]"),
  ]
    .filter((character) => {
      const code = character.charCodeAt(0);
      return (
        code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127)
      );
    })
    .join("")
    .slice(0, MAX_STRING_LENGTH);
}

function safePath(value: string) {
  try {
    return new URL(value, window.location.origin).pathname.slice(0, 500);
  } catch {
    return value.split(/[?#]/, 1)[0].slice(0, 500);
  }
}

export function sanitizeClientDebugValue(
  value: unknown,
  key = "",
  depth = 0,
): unknown {
  if (SENSITIVE_KEY.test(key)) return "[redacted]";
  if (depth >= MAX_DEPTH) return "[depth-limited]";
  if (
    value === null ||
    value === undefined ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "string") {
    return /(?:url|path|source|route)/i.test(key)
      ? safePath(value)
      : safeString(value);
  }
  if (value instanceof Error) {
    return {
      name: safeString(value.name),
      message: safeString(value.message),
      stack: value.stack ? safeString(value.stack) : undefined,
    };
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeClientDebugValue(item, "", depth + 1));
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, 60)
        .map(([childKey, childValue]) => [
          childKey,
          sanitizeClientDebugValue(childValue, childKey, depth + 1),
        ]),
    );
  }
  return safeString(String(value));
}

function errorDetails(error: unknown) {
  if (error instanceof Error) {
    return sanitizeClientDebugValue(error);
  }
  return {
    name: "UnknownError",
    message: safeString(String(error)),
  };
}

function shouldSend(event: ClientDebugEvent) {
  const fingerprint = JSON.stringify([
    event.event,
    event.level,
    sanitizeClientDebugValue(event.details),
    window.location.pathname,
  ]).slice(0, 4_000);
  const now = Date.now();
  const previous = recentFingerprints.get(fingerprint);
  recentFingerprints.set(fingerprint, now);
  if (recentFingerprints.size > 100) {
    for (const [key, timestamp] of recentFingerprints) {
      if (now - timestamp > DUPLICATE_WINDOW_MS) recentFingerprints.delete(key);
    }
  }
  return previous === undefined || now - previous > DUPLICATE_WINDOW_MS;
}

export function reportClientDebugEvent(event: ClientDebugEvent) {
  if (typeof window === "undefined" || !shouldSend(event)) return;
  const payload = {
    event: event.event,
    level: event.level ?? "info",
    occurredAt: new Date().toISOString(),
    route: window.location.pathname,
    details: sanitizeClientDebugValue(event.details),
  };
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5_000);
  void fetch("/api/debug/client-event", {
    method: "POST",
    credentials: "same-origin",
    keepalive: true,
    signal: controller.signal,
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify(payload),
  })
    // fetch resolves at the headers. Consume the small acknowledgement as well
    // so the diagnostic request does not retain an unread response stream.
    .then((response) => response.text())
    .catch(() => {
      // Diagnostics must not create a second application failure or wait forever.
    })
    .finally(() => window.clearTimeout(timeout));
}

export function installGlobalClientObservability() {
  if (
    typeof window === "undefined" ||
    window.__enkeyClientObservabilityInstalled
  ) {
    return;
  }
  window.__enkeyClientObservabilityInstalled = true;

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target;
      if (target instanceof HTMLElement && target !== window.document.body) {
        const source =
          target instanceof HTMLScriptElement
            ? target.src
            : target instanceof HTMLLinkElement
              ? target.href
              : target instanceof HTMLImageElement
                ? target.currentSrc || target.src
                : "";
        reportClientDebugEvent({
          event: "resource.error",
          level: "error",
          details: {
            tag: target.tagName.toLowerCase(),
            source: source ? safePath(source) : undefined,
          },
        });
        return;
      }
      reportClientDebugEvent({
        event: "runtime.error",
        level: "error",
        details: {
          message: event.message,
          source: event.filename ? safePath(event.filename) : undefined,
          line: event.lineno,
          column: event.colno,
          error: errorDetails(event.error),
        },
      });
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    reportClientDebugEvent({
      event: "runtime.unhandled_rejection",
      level: "error",
      details: { error: errorDetails(event.reason) },
    });
  });
}
